import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { X, Building2, MapPin, Phone, Mail, Calendar, ExternalLink, AlertTriangle, TrendingUp, TrendingDown, Minus, Download, Star } from 'lucide-react'
import { Business, TLBHScore } from '@/types'
import { businessService, ScoreHistoryItem, RedFlagItem } from '@/services/businessService'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { generateBusinessReport } from '@/utils/reportGenerator'
import { useToast } from '@/components/ui/MobileToast'

interface BusinessSearchResult {
  business: Business
  score: TLBHScore
  riskLevel: 'low' | 'medium' | 'high' | 'critical'
  lastUpdated: string
  alerts: number
  trend: 'improving' | 'stable' | 'declining'
}

interface BusinessQuickViewProps {
  business: BusinessSearchResult
  onClose: () => void
}

export function BusinessQuickView({ business, onClose }: BusinessQuickViewProps) {
  const navigate = useNavigate()
  const showToast = useToast()
  const [activeTab, setActiveTab] = useState<'overview' | 'dimensions' | 'alerts' | 'history'>('overview')
  const [scoreHistory, setScoreHistory] = useState<ScoreHistoryItem[]>([])
  const [isLoadingHistory, setIsLoadingHistory] = useState(false)
  const [historyError, setHistoryError] = useState<string | null>(null)
  const [redFlags, setRedFlags] = useState<RedFlagItem[]>([])
  const [isLoadingRedFlags, setIsLoadingRedFlags] = useState(false)
  const [redFlagsError, setRedFlagsError] = useState<string | null>(null)
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false)

  // Load score history and red flags immediately when component mounts
  useEffect(() => {
    loadScoreHistory()
    loadRedFlags()
  }, [business.business.id])

  const loadScoreHistory = async () => {
    setIsLoadingHistory(true)
    setHistoryError(null)
    try {
      const response = await businessService.getScoreHistory(business.business.id, 12)
      setScoreHistory(response.history || [])
    } catch (error) {
      console.error('Failed to load score history:', error)
      setHistoryError('Unable to load score history. Please try again.')
    } finally {
      setIsLoadingHistory(false)
    }
  }

  const loadRedFlags = async () => {
    setIsLoadingRedFlags(true)
    setRedFlagsError(null)
    try {
      const response = await businessService.getRedFlags(business.business.id, 'active')
      setRedFlags(response.redFlags || [])
    } catch (error) {
      console.error('Failed to load red flags:', error)
      setRedFlagsError('Unable to load alerts. Please try again.')
    } finally {
      setIsLoadingRedFlags(false)
    }
  }

  const handleDownloadReport = async () => {
    setIsGeneratingPDF(true)
    try {
      // Convert business data to API format for report generation
      const businessData = {
        business_name: business.business.name,
        pan: business.business.pan,
        gstin: business.business.gstin,
        business_type: business.business.businessType,
        industry_sector: business.business.industry,
        registration_date: business.business.registrationDate,
        contact_email: business.business.contactEmail,
        contact_phone: business.business.contactPhone,
        registered_address: `${business.business.address.street}, ${business.business.address.city}, ${business.business.address.state} ${business.business.address.pincode}`,
        score: business.score.tlbhScore.toString(),
        band: business.score.band,
        confidence: business.score.confidence.toString()
      }
      
      await generateBusinessReport(businessData, scoreHistory, redFlags)
      showToast.showToast({
        type: 'success',
        title: 'Report Downloaded',
        message: 'Business report has been downloaded successfully.'
      })
    } catch (error) {
      console.error('Failed to generate report:', error)
      showToast.showToast({
        type: 'error',
        title: 'Download Failed',
        message: 'Unable to generate report. Please try again.'
      })
    } finally {
      setIsGeneratingPDF(false)
    }
  }

  const handleViewFullReport = () => {
    navigate(`/lender/business/${business.business.id}`)
    onClose()
  }

  const getRiskLevelColor = (riskLevel: string) => {
    switch (riskLevel) {
      case 'low': return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900'
      case 'medium': return 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900'
      case 'high': return 'text-orange-600 bg-orange-100 dark:text-orange-400 dark:bg-orange-900'
      case 'critical': return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900'
      default: return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-900'
    }
  }

  const getBandColor = (band: string) => {
    switch (band) {
      case 'A+':
      case 'A': return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900'
      case 'B+':
      case 'B': return 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900'
      case 'C+':
      case 'C': return 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900'
      case 'D': return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900'
      default: return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-900'
    }
  }

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'improving': return <TrendingUp className="w-4 h-4 text-green-600" />
      case 'declining': return <TrendingDown className="w-4 h-4 text-red-600" />
      case 'stable': return <Minus className="w-4 h-4 text-gray-600" />
      default: return null
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const getBusinessTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      sole_proprietorship: 'Sole Proprietorship',
      partnership: 'Partnership',
      private_limited: 'Private Limited',
      public_limited: 'Public Limited',
      llp: 'Limited Liability Partnership'
    }
    return types[type] || type
  }

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'dimensions', label: 'Dimensions' },
    { id: 'alerts', label: 'Alerts', count: redFlags.length },
    { id: 'history', label: 'History' }
  ]

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block w-full max-w-4xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-lg">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {business.business.name}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {business.business.industry} • {getBusinessTypeLabel(business.business.businessType)}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={handleDownloadReport}
                disabled={isGeneratingPDF}
                className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                {isGeneratingPDF ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Report
                  </>
                )}
              </button>
              
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Score Summary */}
          <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-900 dark:text-white">
                  {parseFloat(business.score.tlbhScore.toString()).toFixed(2)}
                </div>
                <div className={`inline-flex items-center px-2 py-1 rounded-full text-sm font-medium ${getBandColor(business.score.band)} mt-1`}>
                  {business.score.band}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  TLBH Score
                </div>
              </div>
              
              <div className="text-center">
                <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getRiskLevelColor(business.riskLevel)}`}>
                  {business.riskLevel.charAt(0).toUpperCase() + business.riskLevel.slice(1)} Risk
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Risk Level
                </div>
              </div>
              
              <div className="text-center">
                <div className="flex items-center justify-center space-x-1">
                  {getTrendIcon(business.trend)}
                  <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                    {business.trend}
                  </span>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Trend
                </div>
              </div>
              
              <div className="text-center">
                <div className="text-lg font-semibold text-gray-900 dark:text-white">
                  {parseFloat(business.score.confidence.toString()).toFixed(0)}%
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Confidence
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex px-6">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`py-3 px-4 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  {tab.label}
                  {tab.count !== undefined && tab.count > 0 && (
                    <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                      activeTab === tab.id
                        ? 'bg-primary-100 text-primary-600 dark:bg-primary-900 dark:text-primary-400'
                        : 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-300'
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="px-6 py-6 max-h-96 overflow-y-auto">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Business Details */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Business Information
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2 text-sm">
                        <MapPin className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600 dark:text-gray-400">Address:</span>
                        <span className="text-gray-900 dark:text-white">
                          {business.business.address.street}, {business.business.address.city}, {business.business.address.state} {business.business.address.pincode}
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-2 text-sm">
                        <Phone className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600 dark:text-gray-400">Phone:</span>
                        <span className="text-gray-900 dark:text-white">
                          {business.business.contactPhone}
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-2 text-sm">
                        <Mail className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600 dark:text-gray-400">Email:</span>
                        <span className="text-gray-900 dark:text-white">
                          {business.business.contactEmail}
                        </span>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2 text-sm">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600 dark:text-gray-400">Registered:</span>
                        <span className="text-gray-900 dark:text-white">
                          {formatDate(business.business.registrationDate)}
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-2 text-sm">
                        <span className="text-gray-600 dark:text-gray-400">PAN:</span>
                        <span className="text-gray-900 dark:text-white font-mono">
                          {business.business.pan}
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-2 text-sm">
                        <span className="text-gray-600 dark:text-gray-400">GSTIN:</span>
                        <span className="text-gray-900 dark:text-white font-mono">
                          {business.business.gstin}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Data Sources */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    Data Sources
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {business.score.dataSourcesUsed.map((source) => (
                      <span
                        key={source}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300"
                      >
                        {source}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'dimensions' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Dimension Breakdown
                </h3>
                <div className="space-y-4">
                  {Object.entries(business.score.dimensionScores).map(([key, dimension]) => (
                    <div key={key} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                          {key}
                        </h4>
                        <div className="flex items-center space-x-2">
                          <span className="text-lg font-semibold text-gray-900 dark:text-white">
                            {parseFloat(dimension.score.toString()).toFixed(2)}
                          </span>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            (Weight: {(dimension.weight * 100).toFixed(0)}%)
                          </span>
                        </div>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-2">
                        <div
                          className="bg-primary-600 h-2 rounded-full"
                          style={{ width: `${parseFloat(dimension.score.toString()).toFixed(2)}%` }}
                        />
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {dimension.explanation}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'alerts' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Active Alerts
                </h3>
                {isLoadingRedFlags ? (
                  <div className="text-center py-8">
                    <LoadingSpinner size="md" />
                    <p className="text-gray-600 dark:text-gray-400 mt-4">Loading alerts...</p>
                  </div>
                ) : redFlagsError ? (
                  <div className="text-center py-8">
                    <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-3" />
                    <p className="text-red-600 dark:text-red-400">{redFlagsError}</p>
                    <button
                      onClick={loadRedFlags}
                      className="mt-4 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md text-sm font-medium"
                    >
                      Retry
                    </button>
                  </div>
                ) : redFlags.length > 0 ? (
                  <div className="space-y-3">
                    {redFlags.map((flag) => {
                      const getSeverityColor = (impact: number) => {
                        if (impact >= 15) return 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20'
                        if (impact >= 10) return 'border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/20'
                        return 'border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-900/20'
                      }
                      
                      const getSeverityText = (impact: number) => {
                        if (impact >= 15) return 'Critical'
                        if (impact >= 10) return 'High'
                        if (impact >= 5) return 'Medium'
                        return 'Low'
                      }
                      
                      return (
                        <div key={flag.id} className={`border rounded-lg p-4 ${getSeverityColor(flag.scoreImpact)}`}>
                          <div className="flex items-start space-x-3">
                            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <h4 className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                                  {flag.category.replace(/_/g, ' ')}
                                </h4>
                                <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                                  flag.scoreImpact >= 15 ? 'bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200' :
                                  flag.scoreImpact >= 10 ? 'bg-orange-200 text-orange-800 dark:bg-orange-800 dark:text-orange-200' :
                                  'bg-yellow-200 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200'
                                }`}>
                                  {getSeverityText(flag.scoreImpact)}
                                </span>
                              </div>
                              <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                                {flag.condition}
                              </p>
                              {flag.actionRequired && (
                                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 italic">
                                  Action Required: {flag.actionRequired}
                                </p>
                              )}
                              <div className="flex items-center space-x-4 mt-2 text-xs text-gray-600 dark:text-gray-400">
                                <span>Impact: -{flag.scoreImpact} points</span>
                                <span>Detected: {formatDate(flag.detectedAt)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Star className="w-6 h-6 text-green-600 dark:text-green-400" />
                    </div>
                    <p className="text-gray-600 dark:text-gray-400">
                      No active alerts. This business has a clean record.
                    </p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'history' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Score History
                </h3>
                {isLoadingHistory ? (
                  <div className="text-center py-8">
                    <LoadingSpinner size="md" />
                    <p className="text-gray-600 dark:text-gray-400 mt-4">Loading history...</p>
                  </div>
                ) : historyError ? (
                  <div className="text-center py-8">
                    <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-3" />
                    <p className="text-red-600 dark:text-red-400">{historyError}</p>
                    <button
                      onClick={loadScoreHistory}
                      className="mt-4 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md text-sm font-medium"
                    >
                      Retry
                    </button>
                  </div>
                ) : scoreHistory.length > 0 ? (
                  <div className="space-y-4">
                    {/* Score trend chart visualization */}
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-4">
                        Score Trend (Last 12 Months)
                      </h4>
                      <div className="space-y-2">
                        {scoreHistory.map((item, index) => {
                          const prevScore = index < scoreHistory.length - 1 ? scoreHistory[index + 1].score : item.score
                          const change = item.score - prevScore
                          const changePercent = prevScore > 0 ? ((change / prevScore) * 100).toFixed(1) : '0.0'
                          
                          return (
                            <div key={item.id} className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
                              <div className="flex items-center space-x-3">
                                <div className="text-sm text-gray-600 dark:text-gray-400 w-32">
                                  {new Date(item.timestamp).toLocaleDateString('en-IN', { 
                                    year: 'numeric', 
                                    month: 'short',
                                    day: 'numeric'
                                  })}
                                </div>
                                <div className="flex items-center space-x-2">
                                  <span className="text-lg font-semibold text-gray-900 dark:text-white">
                                    {item.score.toFixed(2)}
                                  </span>
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                    item.band === 'S' || item.band === 'A' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
                                    item.band === 'B' || item.band === 'C' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' :
                                    'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                                  }`}>
                                    {item.band}
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                {change !== 0 && (
                                  <div className={`flex items-center space-x-1 text-sm ${
                                    change > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                                  }`}>
                                    {change > 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                                    <span>{Math.abs(change).toFixed(2)} ({changePercent}%)</span>
                                  </div>
                                )}
                                {change === 0 && (
                                  <div className="flex items-center space-x-1 text-sm text-gray-600 dark:text-gray-400">
                                    <Minus className="w-4 h-4" />
                                    <span>No change</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                    
                    {/* Summary stats */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-gray-900 dark:text-white">
                          {scoreHistory[0]?.score.toFixed(2)}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Current Score
                        </div>
                      </div>
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-gray-900 dark:text-white">
                          {(scoreHistory.reduce((sum, item) => sum + item.score, 0) / scoreHistory.length).toFixed(2)}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Average Score
                        </div>
                      </div>
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 text-center">
                        <div className={`text-2xl font-bold ${
                          scoreHistory[0]?.score > scoreHistory[scoreHistory.length - 1]?.score 
                            ? 'text-green-600 dark:text-green-400' 
                            : scoreHistory[0]?.score < scoreHistory[scoreHistory.length - 1]?.score
                            ? 'text-red-600 dark:text-red-400'
                            : 'text-gray-600 dark:text-gray-400'
                        }`}>
                          {scoreHistory.length >= 2 
                            ? (scoreHistory[0].score - scoreHistory[scoreHistory.length - 1].score).toFixed(2)
                            : '0.00'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          12-Month Change
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 dark:text-gray-400">
                      No historical data available for this business.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Last updated: {formatDate(business.lastUpdated)}
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Close
                </button>
                <button 
                  onClick={handleViewFullReport}
                  className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md text-sm font-medium"
                >
                  View Full Report
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}